import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  CONTACT_DETAIL_PROVIDER,
  PROFILE_PAGE_PROVIDER,
  ProfileService,
  externalProfileOnlyGuard,
  profileContactIdResolver,
  profileResolver,
  userProfileV1Resolver,
} from '@data-access/profiles';
import { ContactService } from '@data-access/profiles/services/contact.service';
import { ContactFacadeService } from '@data-access/profiles/v1/facade';
import { OrganizationService } from '@data-access/organizations';

import {
  permissionProviders,
  SITE_LIST_PROVIDER,
  SiteListService,
  STUDY_LIST_PROVIDER,
  StudyListService,
} from '@data-access/shared';
import { PEOPLE_ADMIN_PERMISSIONS } from '@data-access/permissions';
import { OktaAuthGuard } from '../guards/okta-auth';
import { ContactDetailPageComponent } from '@profiles/pages';
import { WcgTableService } from '@wcg/frontend-framework';
import { siteListResolver } from '@data-access/shared/resolvers/site.resolver';
import { StudyListFacadeService } from '@data-access/shared/facades/study-list.facade.service';
import { studyListResolver } from '@data-access/shared/resolvers/study.resolver';
import {
  canMatchUseSiteServiceV2,
  negateGuard,
} from '../profiles/utils/user-management-route.util';

// NOTE: This routing module is for the "User" section of the app, which includes the user's profile and related pages. It is separate from the "Admin" section, which has its own routing module. The 'profile' route has a V2 path (USER_SITE_SERVICE_V2 ON) and a V1 fallback (USER_SITE_SERVICE_V2 OFF). The 'downloads-exports' and 'resources' routes are also part of this module but are separate from the profile routes.
const routes: Routes = [
  // V2: User profile page (when USER_SITE_SERVICE_V2 is ON)
  {
    component: ContactDetailPageComponent,
    path: 'profile',
    canMatch: [canMatchUseSiteServiceV2, OktaAuthGuard],
    providers: [
      CONTACT_DETAIL_PROVIDER,
      PROFILE_PAGE_PROVIDER,
      ContactService,
      ProfileService,
      ...permissionProviders({ ...PEOPLE_ADMIN_PERMISSIONS() }),
    ],
    data: {
      skipImpersonation: true,
    },
    resolve: {
      resolvedData: profileResolver,
      contactId: profileContactIdResolver,
    },
    children: [
      {
        path: '',
        pathMatch: 'prefix',
        redirectTo: 'overview',
      },
      {
        loadComponent: () =>
          import('../profiles/features/contact-overview').then(
            (m) => m.ProfileOverviewComponent,
          ),
        path: 'overview',
        canMatch: [OktaAuthGuard],
        data: {
          canEdit: true,
        },
      },
      {
        data: {
          type: 'contact',
        },
        loadComponent: () =>
          import('../iris-sites/pages').then((m) => m.SitePageComponent),
        path: 'sites',
        providers: [SITE_LIST_PROVIDER, SiteListService, WcgTableService],
        resolve: {
          resolvedData: siteListResolver,
        },
        canMatch: [externalProfileOnlyGuard],
      },
      {
        data: {
          type: 'contact',
        },
        loadComponent: () =>
          import('../study/pages').then((m) => m.StudyPageComponent),
        path: 'studies',
        providers: [
          STUDY_LIST_PROVIDER,
          StudyListFacadeService,
          StudyListService,
          WcgTableService,
        ],
        resolve: {
          resolvedData: studyListResolver,
        },
        canMatch: [externalProfileOnlyGuard],
      },
    ],
  },
  // V1: User profile page (when USER_SITE_SERVICE_V2 is OFF)
  {
    loadComponent: () =>
      import('../profiles/v1/pages/contact-detail-v1.page').then(
        (m) => m.ContactDetailV1PageComponent,
      ),
    path: 'profile',
    canMatch: [negateGuard(canMatchUseSiteServiceV2), OktaAuthGuard],
    providers: [
      CONTACT_DETAIL_PROVIDER,
      PROFILE_PAGE_PROVIDER,
      ProfileService,
      ContactService,
      ContactFacadeService,
      ...permissionProviders({ ...PEOPLE_ADMIN_PERMISSIONS() }),
    ],
    data: {
      skipImpersonation: true,
    },
    resolve: {
      resolvedData: userProfileV1Resolver,
    },
    children: [
      {
        path: '',
        pathMatch: 'prefix',
        redirectTo: 'overview',
      },
      {
        loadComponent: () =>
          import('../profiles/v1/features/contact-overview/profile-overview-v1.component').then(
            (m) => m.ProfileOverviewV1Component,
          ),
        path: 'overview',
        canMatch: [OktaAuthGuard],
      },
      {
        data: {
          type: 'contact',
        },
        loadComponent: () =>
          import('../iris-sites/pages').then((m) => m.SitePageComponent),
        path: 'sites',
        providers: [SITE_LIST_PROVIDER, OrganizationService, WcgTableService],
        resolve: {
          resolvedData: siteListResolver,
        },
        canMatch: [externalProfileOnlyGuard],
      },
      {
        data: {
          type: 'contact',
        },
        loadComponent: () =>
          import('../study/pages').then((m) => m.StudyPageComponent),
        path: 'studies',
        providers: [
          STUDY_LIST_PROVIDER,
          StudyListFacadeService,
          OrganizationService,
          WcgTableService,
        ],
        resolve: {
          resolvedData: studyListResolver,
        },
        canMatch: [externalProfileOnlyGuard],
      },
    ],
  },
  {
    loadChildren: () =>
      import('../downloads-exports').then((m) => m.DOWNLOAD_EXPORT_ROUTES),
    path: 'downloads-exports',
    pathMatch: 'prefix',
    title: 'Downloads Exports',
  },
  {
    loadChildren: () =>
      import('./resource').then((m) => m.RESOURCE_PAGE_ROUTES),
    path: 'resources',
    pathMatch: 'prefix',
    title: 'Resources',
  },
  {
    loadComponent: () =>
      import('./privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent,
      ),
    path: 'privacy-policy',
    title: 'Privacy Policy',
  },
];

@NgModule({
  exports: [RouterModule],
  imports: [RouterModule.forChild(routes)],
})
export class UserRoutingModule {}
